import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLine, ease } from "./Reveal";
import { scrollToId } from "./SmoothScroll";

const HeroFrame = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div ref={ref} className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={onLeave}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(18% 22% 18% 22% round 48px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 32px)" }}
        transition={{ duration: 1.6, delay: 0.5, ease }}
        className="relative h-[52vh] min-h-[340px] overflow-hidden rounded-[32px] bg-sky-200 shadow-[0_60px_120px_-60px_rgba(11,79,138,0.6)] sm:h-[64vh]"
        data-testid="hero-image-frame"
      >
        <motion.img
          src="/img/hero-raaf.jpg"
          alt="C-130J Super Hercules and Black Hawk helicopter in Australian kangaroo roundel markings flying together through a blue sky"
          style={{ y, scale: 1.14 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-sky-950/35 via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex flex-wrap items-end justify-between gap-3 sm:bottom-8 sm:left-8 sm:right-8">
          <div className="rounded-2xl border border-white/40 bg-white/20 px-4 py-3 text-white backdrop-blur-xl" data-testid="hero-location-chip">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sky-100">Registered office</p>
            <p className="font-display text-base">Surry Hills, NSW — Australia</p>
          </div>
          <div className="hidden rounded-full border border-white/40 bg-white/20 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white backdrop-blur-xl sm:block">
            034° 45′ 27.2″ S · 149° 41′ 54.6″ E
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section id="top" className="relative overflow-hidden pb-16 pt-32 sm:pt-40" data-testid="hero-section">
    <div className="sky-wash pointer-events-none absolute inset-0 -z-10" />
    <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.1 }}
        className="mb-8 flex flex-wrap items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-sky-800"
      >
        <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-sky-500" />
        Aero Soft Solutions Pty Ltd
        <span className="hidden text-slate-400 sm:inline">/</span>
        <span className="hidden text-slate-600 sm:inline">ABN 147 024 828 80</span>
        <span className="hidden text-slate-400 sm:inline">/</span>
        <span className="hidden text-slate-600 sm:inline">ACN 702 482 880</span>
      </motion.div>

      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <h1 className="font-display text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.035em] text-ink sm:text-6xl lg:col-span-8 lg:text-[5.4rem]" data-testid="hero-headline">
          <MaskLine delay={0.25}>Aviation solutions</MaskLine>
          <MaskLine delay={0.4}>at the speed of</MaskLine>
          <MaskLine delay={0.55}>
            <span className="font-serif font-normal italic text-sky-600">capability</span> needs.
          </MaskLine>
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease }}
          className="lg:col-span-4 lg:pb-3"
        >
          <p className="max-w-sm text-base leading-relaxed text-slate-600 md:text-lg" data-testid="hero-subtext">
            We specialise in delivering aviation solutions that move as fast as the mission demands — from first conversation to capability in service.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              onClick={() => scrollToId("contact")}
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-sky-600 px-6 py-3.5 text-sm font-medium text-white shadow-[0_14px_30px_-12px_rgba(2,132,199,0.8)] transition-colors hover:bg-sky-700"
            >
              Discuss your capability need
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => scrollToId("services")}
              data-testid="hero-services-button"
              className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/60 px-6 py-3.5 text-sm font-medium text-ink backdrop-blur transition-colors hover:border-sky-600 hover:text-sky-700"
            >
              Our services
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </div>
        </motion.div>
      </div>

      <div className="mt-14 sm:mt-16">
        <HeroFrame />
      </div>
    </div>
  </section>
);
